import React, { useState, useRef, useEffect } from 'react';
import { Coffee, Sun, ChefHat } from 'lucide-react';
import { getRecipeImageUrlCandidates } from '@/utils/recipeImageStorage';

interface RecipeThumbnailProps {
  imageUrl?: string;
  storageImageUrl?: string;
  userImageUrl?: string;
  storageImagePath?: string;
  userImagePath?: string;
  recipeName: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  mealType?: 'breakfast' | 'lunch' | 'dinner';
}

const RecipeThumbnail: React.FC<RecipeThumbnailProps> = ({
  imageUrl,
  storageImageUrl,
  userImageUrl,
  storageImagePath,
  userImagePath,
  recipeName,
  size = 'sm',
  className = '',
  mealType
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-12 h-12',
    lg: 'w-14 h-14'
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Load image directly without caching
  useEffect(() => {
    if (!isVisible) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const loadCandidate = (candidate: string) => {
      return new Promise<string>((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          resolve(candidate);
        };

        img.onerror = () => {
          reject(new Error(`Failed to load recipe thumbnail: ${candidate}`));
        };

        img.src = candidate;
      });
    };

    const loadImage = async () => {
      const candidates = await getRecipeImageUrlCandidates({
        title: recipeName,
        userImagePath,
        userImageUrl,
        storageImagePath,
        storageImageUrl,
        imageUrl,
      });

      for (const candidate of candidates) {
        try {
          const loadedSrc = await loadCandidate(candidate);
          if (!cancelled) {
            setImageSrc(loadedSrc);
            setIsLoading(false);
          }
          return;
        } catch (error) {
          console.warn(error);
        }
      }

      if (!cancelled) {
        setImageSrc(null);
        setIsLoading(false);
      }
    };

    loadImage();

    return () => {
      cancelled = true;
    };
  }, [isVisible, userImagePath, userImageUrl, storageImagePath, storageImageUrl, imageUrl, recipeName]);

  // Get meal icon based on meal type
  const getMealIcon = () => {
    if (mealType === 'breakfast') {
      return <Coffee className="h-5 w-5 text-accent-foreground" />;
    } else if (mealType === 'lunch') {
      return <Sun className="h-5 w-5 text-primary-foreground" />;
    } else {
      return <ChefHat className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getMealIconBg = () => {
    if (mealType === 'breakfast') {
      return 'bg-accent/20';
    } else if (mealType === 'lunch') {
      return 'bg-primary/20';
    } else {
      return 'bg-muted/40';
    }
  };

  return (
    <div
      ref={imgRef}
      className={`${sizeClasses[size]} rounded-md overflow-hidden flex-shrink-0 ${className}`}
    >
      {isLoading ? (
        // Skeleton loading state
        <div className="w-full h-full bg-muted animate-pulse rounded-md" />
      ) : imageSrc ? (
        // Loaded image
        <img
          src={imageSrc}
          alt={recipeName}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        // Fallback with meal icon
        <div className={`w-full h-full ${getMealIconBg()} flex items-center justify-center`}>
          {getMealIcon()}
        </div>
      )}
    </div>
  );
};

export default RecipeThumbnail;
